import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import { operatorList } from "../../constants/Operators";
import { circleList } from "../../constants/Circles";
import RechargeService from "../../services/RechargeService";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import DthInfoModal from "./DthInfoModal";
import BannerSlider from "./BannerSlider";
import PlanModal from "./PlansModal";
import storage from "../../utils/storage";

const RechargeForm = (props) => {
  const { type } = props;

  const location = useLocation(); // to read navigation state

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [visibleNumberParam, setVisibleNumberParam] = useState(true);
  const [rechargeType, setRechargeType] = useState("prepaid");
  const [number, setNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [searchCircle, setSearchCircle] = useState("");
  const [showDthModal, setShowDthModal] = useState(false);

  const [operatorListArr, setOperatorArrList] = useState([]);

  const prepaidOperatorList = operatorList.filter(
    (operator) => operator.type.toLowerCase() === "prepaid",
  );

  const dthOperatorList = operatorList.filter(
    (operator) => operator.type.toLowerCase() === "dth",
  );

  const googlePlayList = operatorList.filter(
    (operator) => operator.type.toLowerCase() === "google_play",
  );

  useEffect(() => {
    if (location.state?.resetForm) {
      // Reset all form states
      setNumber("");
      setAmount("");
      setSelectedOperator(null);
      setSelectedCircle(null);
      setSearchCircle("");
      setPlanDescription("");
      setPlansData(null);
      // ... any other state
      // Clear the flag
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    if (type === "PREPAID") {
      setOperatorArrList(filterOperatorsByType("prepaid"));
    } else if (type === "DTH") {
      setOperatorArrList(filterOperatorsByType("dth"));
    } else if (type === "GOOGLE_PLAY") {
      const googlePlayOps = filterOperatorsByType("google_play");
      setSelectedOperator(googlePlayOps[0]);
      setOperatorArrList(googlePlayOps);
      setVisibleNumberParam(false);
    }
    checkLoginStatus();
  }, [type]);

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const checkLoginStatus = () => {
    // Retrieve access token and name from storage
    const token = storage.getAccessToken();
    if (token) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  };

  const filterOperatorsByType = (type) => {
    return operatorList.filter(
      (operator) => operator.type.toLowerCase() === type.toLowerCase(),
    );
  };

  const formattedCircleList = circleList.map((name, index) => ({
    code: index.toString(),
    name,
  }));

  const fetchOperator = async (num) => {
    if (!isLoggedIn) {
      Swal.fire("Oops!", "Please login to continue", "error");
      return;
    }

    if (num.length < 10) return;
    setLoading(true);
    try {
      const result = await RechargeService.fetchOperator(num);
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;
        const operatorId = data.operator_id.toString();
        const circleName = data.circle;

        const matchedOperator = prepaidOperatorList.find(
          (op) => op.code === operatorId,
        );

        if (matchedOperator) setSelectedOperator(matchedOperator);
        setSelectedCircle(circleName);
        fetchPlans(false, matchedOperator, circleName);
      } else {
        //   Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  const [dthInfoData, setDthInfoData] = useState();

  const fetchDTHCustomerInfo = async () => {
    if (!isLoggedIn) {
      Swal.fire("Oops!", "Please login to continue", "error");
      return;
    }

    if (!number) {
      Swal.fire("Oops!", "Please enter a valid recharge number", "error");
      return;
    }

    if (!selectedOperator) {
      Swal.fire("Oops!", "Please select recharge operator", "error");
      return;
    }

    setLoading(true);
    try {
      const result = await RechargeService.dthCustomerInfo(
        number,
        selectedOperator.code,
      );
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;

        setDthInfoData(data);
        setShowDthModal(true);
      } else {
        //  Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  // const [showPlanModal, setShowPlanModal] = useState(false);
  const [plansData, setPlansData] = useState();

  const fetchPlans = async (
    shouldLoading,
    selectedOperatorValue,
    circleNameValue,
  ) => {
    if (!isLoggedIn) {
      Swal.fire("Oops!", "Please login to continue", "error");
      return;
    }

    let localPlansData;

    if (!number) {
      Swal.fire("Oops!", "Please enter a valid recharge number", "error");
      return;
    }

    if (!selectedOperator) {
      if (!selectedOperatorValue) {
        Swal.fire("Oops!", "Please select recharge operator", "error");
        return;
      }
    }

    if (!selectedCircle) {
      if (!circleNameValue) {
        Swal.fire("Oops!", "Please select circle", "error");
        return;
      }
    }

    if (shouldLoading) setLoading(true);
    try {
      const result = await RechargeService.plans(
        number,
        selectedOperator
          ? selectedOperator.code
          : selectedOperatorValue
            ? selectedOperatorValue.code
            : "",
        selectedCircle
          ? selectedCircle
          : circleNameValue
            ? circleNameValue
            : "",
      );
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;

        localPlansData = data;
        setPlansData(data);
        //  setShowPlanModal(true);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      if (shouldLoading) setLoading(false);
    }

    return localPlansData;
  };

  const [planDescription, setPlanDescription] = useState();

  const getPlanDescription = async (value) => {
    if (type !== "PREPAID") {
      return;
    }

    if (!value) {
      setPlanDescription("");
      return;
    }

    let localPlansData = plansData;

    if (!localPlansData) {
      localPlansData = await fetchPlans(false); // <-- make sure this returns the data
    }

    const allPlansTab = localPlansData?.find(
      (tab) => tab.tab_name === "All Plans",
    );

    if (!allPlansTab) {
      setPlanDescription("");
      return;
    }

    const plans = allPlansTab?.data.find(
      (data) => data.amount.toString() === value.toString(),
    );

    setPlanDescription(plans?.planDescription || null);
  };

  const rechargeSubmitHandler = () => {
    if (!isLoggedIn) {
      Swal.fire("Oops!", "Please login to continue", "error");
      return;
    }

    if (!number) {
      if (type == "GOOGLE_PLAY") {
      } else {
        Swal.fire("Oops!", "Please enter a valid recharge number", "error");
        return;
      }
    }

    if (!selectedOperator) {
      Swal.fire("Oops!", "Please select recharge operator", "error");
      return;
    }

    // if (!selectedCircle) {
    //   Swal.fire("Oops!", "Please select circle", "error");
    //   return;
    // }

    if (!amount) {
      Swal.fire("Oops!", "Please enter recharge amount", "error");
      return;
    }

    navigate("/recharge-order-summary", {
      state: {
        type,
        number,
        amount,
        selectedOperator,
        selectedCircle,
        planDescription,
      },
    });
  };

  const handleRechargeTypeChange = (e) => {
    const type = e.target.id;
    setRechargeType(type);
    if (type === "postpaid") {
      navigate("/bill-payment/postpaid");
    }
  };

  const handleNumberChange = (e) => {
    const num = e.target.value;
    setNumber(num);
    if (num.length === 10 && type === "PREPAID") {
      fetchOperator(num);
    }
  };

  return (
    <React.Fragment>
      {loading && <LoadingScreen />}

      <div className="row g-4">
        <div className="col-lg-4 col-xxl-5">
          <form id="recharge-bill" method="post">
            {type === "PREPAID" && (
              <div className="text-center mb-3">
                <div className="form-check form-check-inline">
                  <input
                    id="prepaid"
                    name="rechargeBillpayment"
                    className="form-check-input"
                    type="radio"
                    checked={rechargeType === "prepaid"}
                    onChange={handleRechargeTypeChange}
                    required
                  />
                  <label className="form-check-label" htmlFor="prepaid">
                    Prepaid
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    id="postpaid"
                    name="rechargeBillpayment"
                    className="form-check-input"
                    type="radio"
                    checked={rechargeType === "postpaid"}
                    onChange={handleRechargeTypeChange}
                    required
                  />
                  <label className="form-check-label" htmlFor="postpaid">
                    Postpaid
                  </label>
                </div>
              </div>
            )}

            <div className="mb-3">
              {visibleNumberParam && (
                <>
                  <input
                    type="text"
                    className="form-control fw-bold fs-5" // Bootstrap: bold + larger font
                    id="mobileNumber"
                    placeholder={
                      type === "PREPAID"
                        ? "Enter Mobile Number"
                        : "Enter Recharge Number"
                    }
                    value={number}
                    onChange={handleNumberChange}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, ""); // removes non-digits
                    }}
                    maxLength={type === "PREPAID" ? 10 : 20}
                    required
                  />
                </>
              )}
              <div className="d-flex justify-content-between align-items-center">
                {type === "DTH" && (
                  <span
                    onClick={fetchDTHCustomerInfo}
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    className="text-primary ms-auto"
                  >
                    Get Customer Name
                  </span>
                )}
              </div>
            </div>

            <div className="mb-3">
              <div className="dropdown">
                <button
                  className="btn w-100 text-start border bg-white d-flex align-items-center justify-content-between"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ boxShadow: "none" }}
                >
                  {selectedOperator ? (
                    <div className="d-flex align-items-center">
                      <img
                        src={selectedOperator.image}
                        alt={selectedOperator.title}
                        width={30}
                        height={30}
                        className="me-2"
                        style={{ objectFit: "contain" }}
                      />
                      <span>{selectedOperator.title}</span>
                    </div>
                  ) : (
                    "Select Operator"
                  )}
                  <i className="bi bi-caret-down-fill ms-auto"></i>
                </button>

                <ul className="dropdown-menu w-100">
                  {operatorListArr.map((operator) => (
                    <li key={operator.code}>
                      <button
                        className="dropdown-item d-flex align-items-center"
                        type="button"
                        onClick={() => setSelectedOperator(operator)}
                      >
                        <img
                          src={operator.image}
                          alt={operator.title}
                          width={30}
                          className="me-2"
                        />
                        {operator.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Circle Dropdown shown after Operator is selected */}
            {type === "PREPAID" && (
              <div className="mb-3">
                <div className="dropdown">
                  <button
                    className="btn w-100 text-start border bg-white d-flex align-items-center justify-content-between"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ boxShadow: "none" }}
                  >
                    {selectedCircle ? selectedCircle : "Select Circle"}
                    <i className="bi bi-caret-down-fill ms-auto"></i>
                  </button>

                  <ul
                    className="dropdown-menu w-100 p-2"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    <li>
                      <input
                        type="text"
                        className="form-control mb-2"
                        placeholder="Search Circle"
                        value={searchCircle}
                        onChange={(e) => setSearchCircle(e.target.value)}
                      />
                    </li>
                    {formattedCircleList
                      .filter((circle) =>
                        circle.name
                          .toLowerCase()
                          .includes(searchCircle.toLowerCase()),
                      )
                      .map((circle) => (
                        <li key={circle.code}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => setSelectedCircle(circle.name)}
                          >
                            {circle.name}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="input-group mb-3">
              <span className="input-group-text">₹</span>
              {type === "PREPAID" && (
                <a
                  // href="#"
                  onClick={() => {
                    fetchPlans(true);
                  }}
                  //  data-bs-toggle="modal"
                  //  data-bs-target="#view-plans"
                  className="view-plans-link"
                >
                  View Plans
                </a>
              )}
              <input
                className="form-control fw-bold fs-5" // Bootstrap: bold + larger font
                id="amount"
                placeholder="Enter Amount"
                required
                type="text"
                value={amount}
                maxLength={5}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, ""); // removes non-digits
                }}
                onChange={(e) => {
                  let value = e.target.value;

                  setAmount(value);
                  getPlanDescription(value);
                }}
              />
            </div>

            <p>{planDescription ? planDescription : ""}</p>

            <div className="d-grid">
              <button
                type="button"
                className="btn btn-primary"
                onClick={rechargeSubmitHandler}
              >
                Continue to Recharge
              </button>
            </div>
          </form>
        </div>

        <div className="col-lg-8 col-xxl-7">
          {!plansData && <BannerSlider />}
          {plansData && (
            <PlanModal
              plansData={plansData}
              onSelectAmount={(amount) => setAmount(amount)}
              onSelectPlanDescription={(amount) => setPlanDescription(amount)}
            />
          )}
        </div>
      </div>

      <DthInfoModal
        show={showDthModal}
        onClose={() => setShowDthModal(false)}
        info={dthInfoData}
      />
    </React.Fragment>
  );
};

export default RechargeForm;
